import "./datadog";
import "./globals.css";
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  type FetchResult,
  type FieldFunctionOptions,
  type FieldMergeFunction,
  fromPromise,
  HttpLink,
  InMemoryCache,
  type Observable,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { ErrorLink, type ErrorResponse } from "@apollo/client/link/error";
import {
  ClerkProvider,
  useAuth,
  useOrganizationList,
  useUser,
} from "@clerk/clerk-react";
import { RouterContext, SpinnerContext, Toaster } from "@repo/ui";
import { superjsonSerialize } from "@repo/utils";
import { RouterProvider } from "@tanstack/react-router";
import countries from "i18n-iso-countries";
import countriesEn from "i18n-iso-countries/langs/en.json";
import type React from "react";
import { StrictMode, useEffect, useMemo } from "react";
import ReactDOM from "react-dom/client";
import spinnerUrl from "~/assets/animations/primary-spinner.riv";
import { config } from "./config";
import possibleTypes from "./gql/client/possibleTypes";
import { customScalarsLink } from "./gql/customScalars";
import {
  RouterCtxNavLink,
  routerCtxUseTanstackSearchParams,
} from "./pkgui-router-context";
import { router } from "./router";

countries.registerLocale(countriesEn);

const client = new ApolloClient({
  cache: new InMemoryCache({
    possibleTypes: possibleTypes.possibleTypes,
    typePolicies: {
      CurrencyAmount: {
        merge: true,
      },
      Query: {
        fields: {
          externalBankAccountDataInstitutions: {
            keyArgs: (args) =>
              serializeKeyArgs({
                filter: args?.filter,
                distinct: args?.distinct,
              }),
            merge: paginatedMergeFunction("institutions"),
          },
          payouts: {
            keyArgs: (args) =>
              serializeKeyArgs({ filter: args?.filter, order: args?.order }),
            merge: paginatedMergeFunction("payouts"),
          },
          transactions: {
            keyArgs: (args) =>
              serializeKeyArgs({ filter: args?.filter, order: args?.order }),
            merge: paginatedMergeFunction("transactions"),
          },
          transferGroups: {
            keyArgs: (args) =>
              serializeKeyArgs({ filter: args?.filter, order: args?.order }),
            merge: paginatedMergeFunction("transferGroups"),
          },
          transfers: {
            keyArgs: (args) =>
              serializeKeyArgs({ filter: args?.filter, order: args?.order }),
            merge: paginatedMergeFunction("transfers"),
          },
          beneficiaryAccounts: {
            keyArgs: (args) => serializeKeyArgs({ filter: args?.filter }),
            merge: paginatedMergeFunction("beneficiaryAccounts"),
          },
          events: {
            keyArgs: (args) => serializeKeyArgs({ filter: args?.filter }),
            merge: paginatedMergeFunction("events"),
          },
          statementBatches: {
            keyArgs: ["filter"],
            merge: paginatedMergeFunction("statementBatches"),
          },
          statements: {
            keyArgs: ["filter"],
            merge: paginatedMergeFunction("statements"),
          },
          ledgerEntries: {
            keyArgs: ["filter"],
            merge: paginatedMergeFunction("ledgerEntries"),
          },
          calastoneTransactions: {
            keyArgs: (args) => serializeKeyArgs({ filter: args?.filter }),
            merge: paginatedMergeFunction("calastoneTransactions"),
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      errorPolicy: "all",
    },
  },
});

function paginatedMergeFunction(
  itemsKey: string,
): FieldMergeFunction<
  any,
  any,
  FieldFunctionOptions<Record<string, any>, Record<string, any>>
> {
  return function merge(existing, incoming) {
    if (!existing || !incoming || !existing[itemsKey]) {
      return incoming;
    }

    const existingRefs = new Set<string>(
      existing[itemsKey].map((i: { __ref: string }) => i.__ref),
    );
    const newItems = incoming[itemsKey].filter(
      (i: { __ref: string }) => !existingRefs.has(i.__ref),
    );

    return {
      ...existing,
      ...incoming,
      [itemsKey]: [...existing[itemsKey], ...newItems],
    };
  };
}

function serializeKeyArgs(args: Record<string, any> | null): string {
  // don't use superjsonStringify directly because it returns an object like { "json": ..., "meta": ... }
  return JSON.stringify(superjsonSerialize(args).json);
}

function ApolloWrapper({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const user = useUser();
  const { setActive } = useOrganizationList();

  const authLink = setContext(async (_, { headers }) => {
    const token = await auth.getToken();
    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : "",
      },
    };
  });

  const setActiveOrg = async (): Promise<void> => {
    if (setActive) {
      await setActive({ organization: config.primaryOpsOrgId });
    }
  };

  const setActiveOrganisationAndRetry = ({
    operation,
    forward,
  }: ErrorResponse): Observable<FetchResult> => {
    return fromPromise(setActiveOrg()).flatMap(() => forward(operation));
  };

  const errorLink = new ErrorLink((err) => {
    if (
      err.networkError &&
      "statusCode" in err.networkError &&
      err.networkError.statusCode === 404
    ) {
      if (
        "result" in err.networkError &&
        typeof err.networkError.result === "object" &&
        err.networkError.result.code === "organisation_not_found"
      ) {
        return setActiveOrganisationAndRetry(err);
      } else if (!auth.orgId) {
        return setActiveOrganisationAndRetry(err);
      } else {
        auth.signOut();
      }
    } else if (
      err.networkError &&
      "statusCode" in err.networkError &&
      err.networkError.statusCode === 403
    ) {
      if (
        "result" in err.networkError &&
        typeof err.networkError.result === "object" &&
        err.networkError.result.code === "mfa_claim_required"
      ) {
        router.navigate({ to: "/user-profile#/security" });
      } else {
        auth.signOut();
      }
    }
  });

  client.setLink(
    ApolloLink.from([
      authLink,
      customScalarsLink,
      (operation, forward) => {
        operation.setContext(
          ({ headers }: { headers: Record<string, string> }) => ({
            headers: {
              "X-Page-Origin": window.location.pathname,
              ...headers,
            },
          }),
        );
        return forward(operation);
      },
      errorLink,
      new HttpLink({
        uri: `${config.apiUrl}/graphql`,
        credentials: "same-origin",
      }),
    ]),
  );

  useEffect(() => {
    if (!user.isSignedIn) {
      client.clearStore();
    }
  }, [user]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}

function RouterWrapper() {
  const pkgUIRouterContext = useMemo(
    () => ({
      NavLink: RouterCtxNavLink,
      useSearchParams: routerCtxUseTanstackSearchParams,
    }),
    [],
  );

  return (
    <SpinnerContext.Provider value={{ spinnerUrl }}>
      <RouterContext.Provider value={pkgUIRouterContext}>
        <RouterProvider router={router} />
      </RouterContext.Provider>
    </SpinnerContext.Provider>
  );
}

const rootElement = document.getElementById("app")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <ClerkProvider
        publishableKey={config.clerkClientKey}
        afterSignOutUrl="/sign-in"
        routerPush={(to) => router.navigate({ to })}
        routerReplace={(to) => router.navigate({ to, replace: true })}
      >
        <ApolloWrapper>
          <Toaster />
          <RouterWrapper />
        </ApolloWrapper>
      </ClerkProvider>
    </StrictMode>,
  );
}
